import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { StableKeySchema } from '../../questions/stable-key/stable-key';
import { QuestionVisibilityPolicySchema } from '../../visibility/resolve-visibility/resolve-visibility';
import { MemberProfileSchema } from '../member-profile';
import { VisibleAnswerSchema } from '../visible-answers';

/**
 * What a member in each viewer context would be shown of this answer — the
 * policy and the member's own preference already resolved, so a staff surface
 * can say "hidden from everyone" or "visible after a match" without running
 * the evaluator itself.
 */
export const StaffMemberVisibilitySchema = z
  .object({
    public: z.boolean(),
    matched: z.boolean(),
  })
  .strict();

export type StaffMemberVisibility = z.infer<typeof StaffMemberVisibilitySchema>;

/**
 * One answer as **staff** read it (P27c1) — the N18 exception, on
 * `members.read`, and the only shape that ever carries a `forced_hidden` answer
 * off the api.
 *
 * The member-facing {@link VisibleAnswerSchema} plus exactly two facts: the
 * question's `policy` and the resolved `memberVisibility`. The first says why
 * other members do not see this (the builder forced it, or the member chose);
 * the second says whether they do. A reviewer looking at a civil registry id
 * beside a passport needs both, and needs nothing else about the row.
 */
export const StaffVisibleAnswerSchema = VisibleAnswerSchema.extend({
  policy: QuestionVisibilityPolicySchema,
  memberVisibility: StaffMemberVisibilitySchema,
}).strict();

export type StaffVisibleAnswer = z.infer<typeof StaffVisibleAnswerSchema>;

/**
 * The answers of one section, in form order — the heading a staff profile reads
 * under (D4's `sectionTitle`, in the operator's locale). A section with nothing
 * answered is **absent**, not drawn empty: the api groups what exists.
 */
export const StaffAnswerSectionSchema = z
  .object({
    sectionId: UuidSchema,
    sectionKey: StableKeySchema,
    title: z.string(),
    answers: z.array(StaffVisibleAnswerSchema).min(1),
  })
  .strict();

export type StaffAnswerSection = z.infer<typeof StaffAnswerSectionSchema>;

/**
 * `GET /admin/users/:id/answers` — everything a member has said about
 * themselves, for the staff surfaces that judge it: the members detail and the
 * verification case. Audited per read (`profile.answers.viewed`).
 *
 * Not a `VisibleAnswers` envelope: there is no viewer context to echo, because
 * the reader is not a member and the payload is not filtered by one.
 */
export const StaffProfileAnswersSchema = z
  .object({
    userId: UuidSchema,
    profile: MemberProfileSchema,
    sections: z.array(StaffAnswerSectionSchema),
  })
  .strict();

export type StaffProfileAnswers = z.infer<typeof StaffProfileAnswersSchema>;
